import React, { useState, useRef, useEffect } from 'react';
import { Building2, Package, Users, ChevronDown, Check, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Module {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  url: string;
  color: string;
  activeColor: string;
}

const CURRENT_MODULE = 'hr';

/**
 * Derive a module's frontend URL.  Priority:
 * 1. Vite env var (VITE_CRM_URL / VITE_INVENTORY_URL / VITE_HR_URL)
 * 2. Production auto-detect from current hostname  (dealbox-{module}-dev.rayfor.lk)
 * 3. Localhost fallback
 */
const MODULE_SLUG: Record<string, string> = {
  crm: 'crm',
  inventory: 'inventry',
  hr: 'hr',
};
const LOCAL_PORTS: Record<string, number> = { crm: 3000, inventory: 3005, hr: 3002 };

function resolveModuleUrl(moduleId: string, envVar: string | undefined): string {
  if (envVar) return envVar;
  const { hostname, protocol } = window.location;
  if (hostname.includes('dealbox-')) {
    const targetSlug = MODULE_SLUG[moduleId];
    const targetHost = hostname.replace(
      /dealbox-[a-z]+-/,
      `dealbox-${targetSlug}-`,
    );
    return `${protocol}//${targetHost}`;
  }
  return `http://localhost:${LOCAL_PORTS[moduleId]}`;
}

const MODULES: Module[] = [
  {
    id: 'crm',
    name: 'CRM',
    description: 'Customer Relationship Management',
    icon: Building2,
    url: resolveModuleUrl('crm', (import.meta as any).env.VITE_CRM_URL),
    color: 'from-blue-400 to-blue-600',
    activeColor: 'bg-blue-500/20',
  },
  {
    id: 'inventory',
    name: 'Inventory',
    description: 'Inventory & Procurement',
    icon: Package,
    url: resolveModuleUrl('inventory', (import.meta as any).env.VITE_INVENTORY_URL),
    color: 'from-emerald-400 to-emerald-600',
    activeColor: 'bg-emerald-500/20',
  },
  {
    id: 'hr',
    name: 'HR',
    description: 'Human Resources',
    icon: Users,
    url: resolveModuleUrl('hr', (import.meta as any).env.VITE_HR_URL),
    color: 'from-violet-400 to-violet-600',
    activeColor: 'bg-violet-500/20',
  },
];

interface ModuleSwitcherProps {
  showLabels: boolean;
}

const ModuleSwitcher: React.FC<ModuleSwitcherProps> = ({ showLabels }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentModule = MODULES.find((m) => m.id === CURRENT_MODULE)!;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleModuleSwitch = (mod: Module) => {
    if (mod.id === CURRENT_MODULE) {
      setDropdownOpen(false);
      return;
    }
    const url = new URL(mod.url);
    window.location.href = url.toString();
  };

  const CurrentIcon = currentModule.icon;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={cn(
          "w-full flex items-center gap-3 group cursor-pointer transition-all duration-200",
          showLabels ? "justify-start" : "justify-center"
        )}
      >
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentModule.color} flex items-center justify-center border border-white/20 shadow-lg flex-shrink-0`}>
          <CurrentIcon className="h-5 w-5 text-white" />
        </div>
        {showLabels && (
          <div className="flex items-center gap-2 min-w-0 transition-all duration-200">
            <span className="font-bold text-xl text-white whitespace-nowrap truncate">
              {currentModule.name}
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-white/60 transition-transform duration-200",
                dropdownOpen && "rotate-180"
              )}
            />
          </div>
        )}
      </button>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-[#0f2847] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          <div className="p-1.5">
            <p className="text-[10px] uppercase tracking-wider text-white/40 px-3 py-1.5 font-medium">
              Switch Module
            </p>
            {MODULES.filter(mod => {
              try {
                const allowedStr = localStorage.getItem('allowed_modules');
                if (!allowedStr) return true;
                const allowed = JSON.parse(allowedStr);
                return allowed[mod.id] !== false; // Only filter out if explicitly false
              } catch { return true; }
            }).map((mod) => {
              const Icon = mod.icon;
              const isCurrent = mod.id === CURRENT_MODULE;
              return (
                <button
                  key={mod.id}
                  onClick={() => handleModuleSwitch(mod)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150",
                    isCurrent
                      ? `${mod.activeColor} text-white`
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  )}
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${mod.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{mod.name}</div>
                    <div className="text-[11px] text-white/40 truncate">{mod.description}</div>
                  </div>
                  {isCurrent ? (
                    <Check className="h-4 w-4 text-white/60 flex-shrink-0" />
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5 text-white/30 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleSwitcher;

/**
 * Call once at app startup (before React renders) to handle cross-module
 * token from URL.  Stores the token in localStorage and cleans the URL.
 * No reload is needed — React has not mounted yet, so AuthContext will
 * read the token from localStorage on its initial mount.
 */
export function handleIncomingModuleToken(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('token')) return;
  params.delete('token');
  const clean = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState({}, '', clean);
}
